import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chip,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  Paper,
  Popover,
  SearchInput,
  Select,
  SelectContent,
  SelectFilterTrigger,
  SelectItem,
  SelectValue,
} from '@c2fo/liquidity';
import colors from '@c2fo/liquidity/colors';
import {
  CalendarLinesIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
  FilterIcon,
  FilterVariantIcon,
  InfoCircleIcon,
  MoneyBillIcon,
  ShapesIcon,
} from '@c2fo/liquidity/icons';
import emptystatements from '@/enterprise/assets/emptystatementsgrey.svg?url';
import EllipsisTooltip from '@/enterprise/components/EllipsisTooltip';
import NoDataState from '@/enterprise/components/NoDataState';
import {
  Table,
  TableBody,
  TableCell,
  TableDisplay,
  TableHead,
  TableHeader,
  TableRow,
  TableSortArrow,
  TableSortDirection,
} from '@/enterprise/components/Table';
import useAutomations, { TakerMarketWithAutomation } from '@/enterprise/data/useAutomations';
import { RecurringRule, RecurringRuleCategory } from '@/enterprise/data/useRecurringRules';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import getMarketType from '@/utils/getMarketType';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useRestrictions from '@/utils/useRestrictions';
import DeleteAllRulesModal from '../recurringRules/components/DeleteAllRulesModal';
import MiniRuleManager from '../recurringRules/components/MiniRuleManager';
import Pagination, { getStartEnd } from '../recurringRules/components/Pagination';
import getRecurringRuleForCategory from '../recurringRules/utils/getRecurringRuleForCategory';
import MiniScheduleManager from './components/MiniScheduleManager';
import sortAutomationsTable, { Sort, SortKey } from './utils/sortAutomationsTable';

type AutomationType = 'all' | 'invoiceRules' | 'offerSchedules';

interface RulesTableFilters {
  currency?: string;
  ruleCategory?: RecurringRuleCategory;
  automationType?: AutomationType;
}

const ruleCategoryFilters: { key: RecurringRuleCategory; translationKey: string }[] = [
  {
    key: 'dpe',
    translationKey: 'core.daysPaidEarly',
  },
  {
    key: 'dueDate',
    translationKey: 'recurringRules.dueDate.category.label',
  },
  {
    key: 'amount',
    translationKey: 'core.invoiceAmount',
  },
  {
    key: 'invoiceId',
    translationKey: 'core.invoiceId',
  },
];

/**
 * sort and filter automations based on search, sort, and filters
 */
const sortAndFilterAutomations = ({
  filters,
  takerMarketsWithAutomations,
  search,
  sort,
}: {
  takerMarketsWithAutomations: TakerMarketWithAutomation[];
  search: string;
  sort: Sort;
  filters?: RulesTableFilters;
}) => {
  return sortAutomationsTable(takerMarketsWithAutomations, sort).filter(({ takerMarket, automation }) => {
    // Do not show renewal schedules as part of automations...yet
    if (takerMarket.offerSchedule?.type === 'renewal') {
      return false;
    }

    if (
      !getTakerMarketName(takerMarket).toLowerCase().includes(search.toLowerCase()) &&
      !getTakerMarketDivisionTitle(takerMarket)?.title.toLowerCase().includes(search.toLowerCase())
    ) {
      return false;
    }

    if (
      filters?.ruleCategory &&
      !getRecurringRuleForCategory(automation.invoiceRule, filters.ruleCategory)?.[
        filters.ruleCategory === 'invoiceId' ? 'excludedVoucherIds' : filters.ruleCategory
      ]
    ) {
      return false;
    }

    if (filters?.currency && takerMarket.currency !== filters.currency) {
      return false;
    }

    if (filters?.automationType === 'invoiceRules' && !automation.invoiceRule) {
      return false;
    }

    if (filters?.automationType === 'offerSchedules' && !automation.offerSchedule) {
      return false;
    }

    return true;
  });
};

const DivisionTitle = ({ takerMarket }: { takerMarket: TakerMarket }) => {
  const { t } = useTranslation();
  const takerMarketName = getTakerMarketName(takerMarket);
  const buyerDivisionName = getTakerMarketDivisionTitle(takerMarket)?.content;
  const isPreferred = getMarketType(takerMarket) === 'PREFERRED';

  return (
    <div className="flex flex-col">
      <EllipsisTooltip
        content={takerMarketName}
        trigger={
          <Link
            className="font-bold"
            to={asEnterpriseLink(
              `customers/${takerMarket.makerOrganizationUuid}/markets/${takerMarket.marketUuid}/division/${takerMarket.takerDivisionId}/invoices/eligible`
            )}
          >
            {takerMarketName}
          </Link>
        }
      />
      <EllipsisTooltip content={buyerDivisionName} trigger={<span className="text-sm">{buyerDivisionName}</span>} />
      {isPreferred && (
        <div className="mt-1 flex gap-1">
          <Popover>
            <Popover.Trigger>
              <InfoCircleIcon className="h-4 w-4" fill={colors.gray[600]} />
            </Popover.Trigger>
            <Popover.Content arrow side="bottom">
              {t('recurringRules.preferredOfferLocked')}
            </Popover.Content>
          </Popover>
          <span className="text-wrap text-xs italic text-text-secondary">{t('core.preferredOffer')}</span>
        </div>
      )}
      <div className="text-sm text-text-primary">{takerMarket.currency}</div>
    </div>
  );
};

const AutomationTable = ({ openCreateAutomationModal }: { openCreateAutomationModal: () => void }) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const takerMarketsWithAutomations = useAutomations();
  const { asCurrency, asNumber } = useLocaleFormat();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [sort, setSort] = useState<Sort>({ key: 'totalAr', direction: 'desc' });
  const [search, setSearch] = useState<string>('');
  const [filters, setFilters] = useState<RulesTableFilters>({
    currency: undefined,
    ruleCategory: undefined,
    automationType: undefined,
  });

  // All automations shown in the table, respective to current filter settings
  const filteredAutomations = sortAndFilterAutomations({ filters, takerMarketsWithAutomations, search, sort });

  // Of all the automation rows shown, these are only the ones that have an invoice rule
  const filteredAutomationsWithInvoiceRules = filteredAutomations.filter(
    ({ automation }) => !!automation.invoiceRule?.id
  );

  const filteredCount = filteredAutomations.length;
  const totalCount = takerMarketsWithAutomations.length;
  const { start, end } = getStartEnd({ limit, page, totalCount });
  const { start: filteredStart, end: filteredEnd } = getStartEnd({ limit, page, totalCount: filteredCount });
  const { getRestrictions } = useRestrictions();
  const { canEditRecurringRules } = getRestrictions([]);
  // delete all rules
  const [openDeleteAllRulesModal, setOpenDeleteAllRulesModal] = useState(false);

  // Just capture the uuids so we can always filter to the latest taker market with automation object below when data changes
  const [expandedTakerMarketUuids, setExpandedTakerMarketUuids] = useState<{
    takerDivisionUuid: string;
    marketUuid: string;
  } | null>(null);

  const expandedTakerMarketWithAutomation = useMemo(() => {
    return takerMarketsWithAutomations.find(
      ({ takerMarket }) =>
        takerMarket.takerDivisionUuid === expandedTakerMarketUuids?.takerDivisionUuid &&
        takerMarket.marketUuid === expandedTakerMarketUuids?.marketUuid
    );
  }, [expandedTakerMarketUuids, takerMarketsWithAutomations]);

  const hasFilterOrSearch = filteredAutomations.length < takerMarketsWithAutomations.length;

  const selectedRulesText =
    filteredCount === 1 ? (
      <div className="text-sm font-medium">
        {search !== '' || !Object.values(filters).every((filter) => filter === undefined)
          ? t('divisions.filterMatchingCount', { filteredCount, start: filteredStart, end: filteredEnd })
          : t('divisions.filterCount', { filteredCount, start: filteredStart, end: filteredEnd })}
      </div>
    ) : (
      <div className="text-sm font-medium">
        {search !== '' || !Object.values(filters).every((filter) => filter === undefined)
          ? t('divisions.filterMatchingCountPlural', { filteredCount, start: filteredStart, end: filteredEnd })
          : t('divisions.filterCountPlural', { filteredCount, start: filteredStart, end: filteredEnd })}
      </div>
    );

  const onPageSizeChange = (limit: number) => {
    setLimit(limit);
    setPage(1);
  };

  const onSort = (accessorKey: SortKey) => {
    let direction: TableSortDirection = accessorKey === 'takerMarketName' ? 'asc' : 'desc';

    if (sort?.key === accessorKey) {
      direction = sort.direction === 'desc' ? 'asc' : 'desc';
    }

    setSort({ key: accessorKey, direction });
  };

  const onSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const tableHeadColumns: { accessorKey?: SortKey; translation: string; className: string }[] = [
    {
      accessorKey: 'takerMarketName',
      translation: t('core.division'),
      className: 'min-w-72',
    },
    {
      accessorKey: 'totalAr',
      translation: t('core.totalAmount'),
      className: 'w-36',
    },
    {
      accessorKey: 'eligibleInvoiceAmount',
      translation: t('core.amountIncluded'),
      className: 'w-40',
    },
    {
      accessorKey: 'eligibleInvoiceCount',
      translation: t('draftOffer.labels.invoicesIncluded'),
      className: 'w-40',
    },
    {
      accessorKey: 'takerExcludedInvoiceAmount',
      translation: t('core.amountExcluded'),
      className: 'w-40',
    },
    {
      accessorKey: 'takerExcludedInvoiceCount',
      translation: t('core.invoicesExcluded'),
      className: 'w-40',
    },
    {
      translation: '',
      className: 'w-24',
    },
  ];

  const handleDeleteAllRulesModalClose = () => {
    setOpenDeleteAllRulesModal(false);
  };

  const handleDeleteAllRulesModalSuccess = async () => {
    setFilters({
      currency: undefined,
      ruleCategory: undefined,
      automationType: undefined,
    });
    setSearch('');
    trackEnterpriseEvent('recurring-rules::bulk-delete::all', {
      referrer: 'rules-manager',
    });
  };

  const handleRuleExpand = (takerMarketWithAutomation: TakerMarketWithAutomation) => {
    if (expandedTakerMarketWithAutomation?.takerMarket.id === takerMarketWithAutomation.takerMarket.id) {
      setExpandedTakerMarketUuids(null);
    } else {
      setExpandedTakerMarketUuids({
        takerDivisionUuid: takerMarketWithAutomation.takerMarket.takerDivisionUuid,
        marketUuid: takerMarketWithAutomation.takerMarket.marketUuid,
      });
    }
  };

  const transformTakerMarketWithAutomationToTakerMarketWithRule = (
    takerMarketWithAutomation: TakerMarketWithAutomation
  ) => {
    const { takerMarket, automation } = takerMarketWithAutomation;
    return {
      takerMarket,
      rule: automation.invoiceRule as RecurringRule,
    };
  };

  return (
    <IntercomDataTarget target="activeAutomationsTable" targetCondition={takerMarketsWithAutomations.length > 0}>
      <DeleteAllRulesModal
        hasFilterOrSearch={hasFilterOrSearch}
        onClose={handleDeleteAllRulesModalClose}
        onSuccess={handleDeleteAllRulesModalSuccess}
        open={openDeleteAllRulesModal}
        recurringRules={filteredAutomationsWithInvoiceRules.map(
          transformTakerMarketWithAutomationToTakerMarketWithRule
        )}
      />

      <h2 className="m-0 pb-4 text-2xl">{t('automation.activeAutomations')}</h2>
      <Paper className="px-8 pt-8" elevation={2}>
        <div className="rounded-t border border-stroke bg-canvas px-6 py-4">
          <div className="flex flex-col justify-between gap-4 lg:flex-row">
            {/* Filters */}
            <div className="flex flex-col items-center justify-between gap-4 lg:flex-row">
              <div className="flex w-full flex-col items-center gap-4 lg:flex-row">
                <div className="flex w-full items-center gap-4">
                  <FilterIcon className="h-5 w-5 shrink-0" fill={colors.text.secondary} />
                  {/* Automation Type Filter */}
                  <Select
                    onValueChange={(value) => {
                      setFilters({
                        ...filters,
                        automationType: value === 'all' ? undefined : (value as AutomationType),
                      });
                      setPage(1);
                    }}
                    value={filters.automationType ?? 'all'}
                  >
                    <SelectFilterTrigger
                      icon={FilterVariantIcon}
                      iconFill="fill-primary-500"
                      label={t('automation.automation')}
                      aria-label={t('automation.automation')}
                      size="sm"
                    >
                      <SelectValue />
                    </SelectFilterTrigger>
                    <SelectContent>
                      {[
                        { key: 'all', label: t('maker.all'), value: 'all' },
                        { key: 'invoiceRules', label: t('automation.invoiceRules'), value: 'invoiceRules' },
                        { key: 'offerSchedules', label: t('automation.offerSchedule'), value: 'offerSchedules' },
                      ].map((item) => (
                        <SelectItem value={item.value} key={item.key}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Rule Category Filter */}
                <Select
                  onValueChange={(value) => {
                    setFilters({
                      ...filters,
                      ruleCategory: value === 'all' ? undefined : (value as RecurringRuleCategory),
                    });
                    setPage(1);
                  }}
                  value={filters.ruleCategory ?? 'all'}
                >
                  <SelectFilterTrigger
                    icon={ShapesIcon}
                    iconFill="fill-primary-500"
                    label={t('recurringRules.ruleCriteria')}
                    size="sm"
                  >
                    <SelectValue />
                  </SelectFilterTrigger>
                  <SelectContent>
                    {[{ key: 'all', label: t('maker.all'), value: 'all' }]
                      .concat(
                        ruleCategoryFilters.map((categories) => ({
                          key: categories.key,
                          value: categories.key,
                          label: t(categories.translationKey),
                        }))
                      )
                      .map((item) => (
                        <SelectItem value={item.value} key={item.key}>
                          {item.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Currency Filter */}
              <Select
                onValueChange={(value) => {
                  setFilters({ ...filters, currency: value === 'all' ? undefined : value });
                  setPage(1);
                }}
                value={filters.currency ?? 'all'}
              >
                <SelectFilterTrigger
                  icon={MoneyBillIcon}
                  iconFill="fill-primary-500"
                  label={t('core.currency')}
                  size="sm"
                >
                  <SelectValue />
                </SelectFilterTrigger>
                <SelectContent>
                  {[{ key: 'all', label: t('maker.all'), value: 'all' }]
                    .concat(
                      takerMarketsWithAutomations
                        .map(({ takerMarket }) => takerMarket.currency)
                        .filter((val, index, curVal) => curVal.indexOf(val) === index)
                        .map((curr) => ({
                          key: curr,
                          label: curr,
                          value: curr,
                        }))
                    )
                    .map((item) => (
                      <SelectItem value={item.value} key={item.key}>
                        {item.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {/* Search and Menu */}
            <div className="inline-flex items-center gap-2">
              <SearchInput
                aria-label={t('core.search')}
                name="Search"
                onChange={(e) => onSearch(e.target.value)}
                onClear={() => onSearch('')}
                placeholder={t('core.search')}
                size="sm"
                value={search}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton
                    icon={EllipsisVerticalIcon}
                    name={t('automation.invoiceRulesMenu')}
                    size="sm"
                    variant="ancillary"
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" avoidCollisions={false} collisionPadding={24}>
                  <DropdownMenuItem
                    disabled={!canEditRecurringRules || filteredAutomationsWithInvoiceRules.length === 0}
                    {...(canEditRecurringRules && { onClick: () => setOpenDeleteAllRulesModal(true) })}
                  >
                    {hasFilterOrSearch
                      ? t('automation.deleteAllFilteredInvoiceRules')
                      : t('automation.deleteAllInvoiceRules')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="pt-4">{selectedRulesText}</div>
        </div>
        <div className="w-full overflow-auto">
          <div className="overflow-auto rounded-b border border-t-0 border-stroke">
            <Table>
              <TableHeader>
                <TableRow>
                  {tableHeadColumns.map(({ accessorKey, translation, className }) => (
                    <TableHead
                      key={translation}
                      className={cn(className)}
                      {...(!!accessorKey && {
                        isSorted: sort?.key === accessorKey,
                        onClick: () => onSort(accessorKey),
                      })}
                    >
                      {!!accessorKey ? (
                        <TableSortArrow
                          accessorKey={accessorKey}
                          sort={sort}
                          {...(!['takerMarketName'].includes(accessorKey) && { textRight: true })}
                        >
                          {translation}
                        </TableSortArrow>
                      ) : (
                        translation
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {takerMarketsWithAutomations.length === 0 ? (
                  <TableDisplay colSpan={tableHeadColumns.length}>
                    <NoDataState
                      className="py-8"
                      icon={<img src={emptystatements} alt="emptystatements" className="h-28 w-28" />}
                      title={t('automation.noActiveAutomations')}
                      message={t('automation.noActiveAutomationsDescription')}
                      action={
                        <Button variant="secondary" onClick={openCreateAutomationModal}>
                          {t('automation.createAutomation')}
                        </Button>
                      }
                    />
                  </TableDisplay>
                ) : filteredAutomations.length === 0 ? (
                  <TableDisplay colSpan={tableHeadColumns.length}>
                    <NoDataState
                      className="py-8"
                      icon={<img src={emptystatements} alt="emptystatements" className="h-28 w-28" />}
                      title={t('taker.noMatchingDivisions')}
                      message={t('taker.noMatchingDivisionsDescription')}
                    />
                  </TableDisplay>
                ) : (
                  filteredAutomations.slice(start - 1, end).map((takerMarketWithAutomation, index) => {
                    const { takerMarket, automation } = takerMarketWithAutomation;

                    return (
                      <Fragment key={takerMarket.id}>
                        <TableRow
                          className={cn({
                            'border-b-0': expandedTakerMarketWithAutomation?.takerMarket.id === takerMarket.id,
                          })}
                        >
                          <TableCell>
                            <div className="space-y-1">
                              <DivisionTitle takerMarket={takerMarket} />
                              <div className="flex flex-wrap items-center gap-2">
                                {!!automation.offerSchedule && (
                                  <Chip
                                    label={
                                      <div className="flex items-center gap-1">
                                        <CalendarLinesIcon
                                          className="h-4 w-4 fill-gray-900"
                                          title="Scheduled Offer Indicator"
                                        />
                                        <div>{t('automation.offerSchedule')}</div>
                                      </div>
                                    }
                                    size="xs"
                                    type="default"
                                    variant="outlined"
                                    isRound
                                  />
                                )}
                                {!!automation.invoiceRule && (
                                  <Chip
                                    label={
                                      <div className="flex items-center gap-1">
                                        <ShapesIcon
                                          className="h-4 w-4"
                                          fill={colors.lightBlue[900]}
                                          title={t('automation.invoiceRules')}
                                        />
                                        <div>{t('automation.invoiceRules')}</div>
                                      </div>
                                    }
                                    size="xs"
                                    type="info"
                                    variant="outlined"
                                    isRound
                                  />
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {asCurrency(
                              takerMarket.eligibleInvoiceAmount +
                                takerMarket.awardedInvoiceAmount +
                                takerMarket.takerExcludedInvoiceAmount,
                              takerMarket.currency
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {asCurrency(
                              takerMarket.eligibleInvoiceAmount + takerMarket.awardedInvoiceAmount,
                              takerMarket.currency
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {asNumber({ value: takerMarket.eligibleInvoiceCount + takerMarket.awardedInvoiceCount })}
                          </TableCell>
                          <TableCell className="text-right">
                            {asCurrency(takerMarket.takerExcludedInvoiceAmount, takerMarket.currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            {asNumber({ value: takerMarket.takerExcludedInvoiceCount })}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end">
                              <IntercomDataTarget target="expandAutomationRow" targetCondition={index === 0}>
                                <IconButton
                                  className={cn({
                                    'rotate-180': expandedTakerMarketWithAutomation?.takerMarket.id === takerMarket.id,
                                  })}
                                  icon={ChevronDownIcon}
                                  name={t('recurringRules.expandRules')}
                                  onClick={() => handleRuleExpand(takerMarketWithAutomation)}
                                  size="md"
                                  variant="ancillary"
                                />
                              </IntercomDataTarget>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedTakerMarketWithAutomation?.takerMarket.id === takerMarket.id && (
                          <TableRow>
                            <TableCell className="space-y-4 pt-0" colSpan={8}>
                              {!!expandedTakerMarketWithAutomation.automation.offerSchedule && (
                                <MiniScheduleManager asChild takerMarketWithAutomation={takerMarketWithAutomation} />
                              )}
                              {!!expandedTakerMarketWithAutomation.automation.invoiceRule && (
                                <MiniRuleManager
                                  asChild
                                  referrer="earlyPayAutomation"
                                  takerMarketWithRule={transformTakerMarketWithAutomationToTakerMarketWithRule(
                                    takerMarketWithAutomation
                                  )}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        <div className="flex justify-center p-4">
          <Pagination
            limit={limit}
            onPageChange={setPage}
            onPageSizeChange={onPageSizeChange}
            page={page}
            totalCount={filteredCount}
          />
        </div>
      </Paper>
    </IntercomDataTarget>
  );
};

export default AutomationTable;
